import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { SEARCH_MIN_LENGTH } from '../../constants/lms-course-review.constants';

const ProgramSearchView = ({
  query,
  onQueryChange,
  programs,
  loading,
  error,
  onOpenProgram,
}) => (
  <Box data-testid="program-search-view">
    <Box mb={2} maxWidth={440}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search by program name"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        data-testid="program-search-input"
        inputProps={{ 'aria-label': 'Search by program name' }}
      />
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        Searching programs — minimum {SEARCH_MIN_LENGTH} characters
      </Typography>
    </Box>

    {loading ? (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    ) : null}

    {error ? (
      <Typography color="error" data-testid="program-search-error">
        {error}
      </Typography>
    ) : null}

    {!loading && !error && query.trim().length >= SEARCH_MIN_LENGTH ? (
      <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
        {programs.length} program{programs.length === 1 ? '' : 's'} matched &quot;{query.trim()}&quot;
      </Typography>
    ) : null}

    <Box display="flex" flexDirection="column" gap={1.5}>
      {programs.map((program) => (
        <Card key={program.resourceId} variant="outlined" data-testid={`program-card-${program.resourceId}`}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Box flex={1}>
                <Typography fontWeight={600}>{program.resourceName}</Typography>
                <Box display="flex" gap={2.5} mt={0.75} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    {program.coverage.total} courses total
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    Pending: {program.coverage.pending}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    Linked: {program.coverage.linked}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Not on Canvas: {program.coverage.notOnCanvas}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                onClick={() => onOpenProgram(program)}
                data-testid={`program-view-courses-${program.resourceId}`}
              >
                View Courses
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  </Box>
);

ProgramSearchView.propTypes = {
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  programs: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onOpenProgram: PropTypes.func.isRequired,
};

ProgramSearchView.defaultProps = {
  loading: false,
  error: null,
};

export default ProgramSearchView;
